import React, { useCallback, useMemo } from 'react';
import { toArray } from 'lodash';
import { Form, FormProps } from '@oscar/theme';
import { selectorFactory } from '../selectors';
import { FormContext, FormContextProps } from '../formContext';
import { FormOptions } from '../typed';
import { useSelector } from 'react-redux';
import { ConcreteForm } from '..';

const useSelectors = ({
  name,
  reducer,
}: {
  name: string;
  reducer?: string;
}) => {
  const store = useSelector((state) => state);
  const selectors = useMemo(
    () => selectorFactory(name, reducer),
    [name, reducer]
  );
  const getDataByKey = useCallback<(key: string) => any>(
    (key: string) => {
      if (key && store) {
        return selectors.getDataByKey(store, key);
      }
      return undefined;
    },
    [selectors, selectors.getData(store)]
  );

  const getInitalValue = useCallback<() => any>(() => {
    if (store) {
      const data = selectors.getInitialValues(store);
      return data;
    }
    return {};
  }, [selectors, store]);

  return [
    {
      getDataByKey,
      getInitalValue,
    },
  ];
};

const CustomizedForm: React.FC<FormProps> = ({
  initialValues,
  children,
  name,
  form,
  ...remain
}) => (
  <Form {...remain} form={form} name={name} initialValues={initialValues}>
    {children}
  </Form>
);

function correctFormName(name: string) {
  const namePath = toArray(name);
  return namePath.join('_');
}

const ComplexForm: React.FC<
  FormProps & { options: FormOptions; form?: ConcreteForm }
> = (props) => {
  const { form: wrapForm, options, children, ...remain } = props;
  const [form] = Form.useForm(wrapForm);
  const [selectors] = useSelectors({
    name: options.name,
    reducer: options?.reducerName,
  });

  const formContextValue = useMemo<FormContextProps>(
    () => ({
      options,
      form,
      selectors,
    }),
    [options, form, selectors]
  );

  const newProps = useMemo(() => {
    return {
      name: correctFormName(options.name),
    };
  }, [options]);

  return (
    <FormContext.Provider value={formContextValue}>
      <CustomizedForm
        {...newProps}
        {...remain}
        form={form}
        initialValues={options?.initialValues!}
      >
        {children}
      </CustomizedForm>
    </FormContext.Provider>
  );
};

export default ComplexForm;
